import type { CompetitorRecord } from "@/lib/compare/types";

const PRICING = "https://www.descript.com/pricing";
const REFUNDS = "https://help.descript.com/hc/en-us/articles/10255871027469-Refund-policy";
const CHECKED = "2026-10-08";

export const DESCRIPT: CompetitorRecord = {
  slug: "descript",
  name: "Descript",
  aliases: ["Descript AI", "descript.com", "Underlord"],
  url: "https://www.descript.com",
  pricingUrl: PRICING,
  category: "podcast",
  positioning:
    "Text-based video and podcast editor with Underlord, an AI co-editor that edits by editing the transcript.",
  plans: [
    {
      name: "Free",
      monthly: 0,
      metered: "1 media hour per month plus 100 one-time AI credits",
      includes: [
        "720p export with a watermark",
        "1 GB per upload, 5 GB storage",
        "100 AI credits once, not renewed monthly",
        "Text-based editor",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
    {
      name: "Hobbyist",
      monthly: 24,
      annualPerMonth: 16,
      metered: "10 media hours per month plus 400 AI credits per month",
      includes: [
        "1080p export, no watermark",
        "10 GB per upload, 100 GB storage",
        "1 seat",
        "Underlord, Studio Sound, Eye Contact and generated media spend credits",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Per person. Annual figure is the per-month rate shown on the pricing page.",
    },
    {
      name: "Creator",
      monthly: 35,
      annualPerMonth: 24,
      metered: "30 media hours per month plus 800 AI credits per month, top-ups allowed",
      includes: [
        "4K export",
        "20 GB per upload, 1 TB storage",
        "1 to 3 seats",
        "Credit top-ups",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Per person. Annual figure is the per-month rate shown on the pricing page.",
    },
    {
      name: "Business",
      monthly: 65,
      annualPerMonth: 50,
      metered: "40 media hours per month plus 1,500 AI credits per month",
      includes: [
        "Up to 5 seats",
        "50 GB per upload, 2 TB storage",
        "Brand Studio",
        "SLA",
      ],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
      note: "Per person. Annual figure is the per-month rate shown on the pricing page.",
    },
    {
      name: "Enterprise",
      custom: true,
      metered: "Custom media hours and credits",
      includes: ["SSO and SCIM", "SOC 2 reporting", "Contact sales pricing"],
      sourceUrl: PRICING,
      checkedAt: CHECKED,
    },
  ],
  entryPlanName: "Hobbyist",
  entryPlanIncludes:
    "10 media hours and 400 AI credits a month, 1080p without a watermark, 100 GB storage, one seat",
  freeTier: "1 media hour a month, 100 one-time AI credits, 720p with a watermark, 5 GB storage",
  trial: "Not stated on the pricing page",
  meteringUnit:
    "Media hours uploaded per month plus AI credits spent on Underlord, Studio Sound, Green Screen, Eye Contact and generated media",
  exportResolution: "720p on Free, 1080p on Hobbyist, 4K on Creator and above",
  features: {
    longToShort: { v: "yes", note: "Create Clips with Underlord" },
    momentRanking: { v: "partial", note: "Underlord picks moments, no ranked list" },
    reframing: { v: "no", note: "Manual crop; Eye Contact and Multicam instead" },
    maxSource: { v: "text", note: "1, 10, 20 or 50 GB per upload by plan" },
    layouts: { v: "text", note: "Caption layouts: Bold, Editorial, Simple, Vintage" },
    redditStory: { v: "no" },
    fakeText: { v: "no" },
    aiImages: { v: "yes", note: "Image and video generation, spends credits" },
    voiceover: { v: "yes", note: "Stock voices and clones, spends credits" },
    voiceCloning: { v: "yes", note: "Overdub-class clones" },
    avatars: { v: "n/a", note: "Not on the pricing page" },
    timelineEditor: { v: "yes", note: "Text editor plus timeline, Free included" },
    captionStyles: { v: "n/a", note: "Count not published; four layouts" },
    captionLanguages: { v: "text", note: "61 for captions, 30 for dubbing" },
    bRoll: { v: "yes", note: "Generated B-roll, spends credits" },
    fourK: { v: "partial", note: "Creator and Business only" },
    scheduling: { v: "no", note: "Publish pages only" },
    analytics: { v: "no" },
    socialTracker: { v: "no" },
    api: { v: "yes", note: "REST API plus an MCP server" },
    webhooks: { v: "n/a", note: "Not stated on the API page" },
    web: { v: "yes" },
    desktop: { v: "yes", note: "Mac (macOS 14+) and Windows 11" },
    mobile: { v: "no", note: "Not advertised" },
    teamSeats: { v: "yes", note: "1 to 3 on Creator, 5 on Business" },
    freeTier: { v: "yes", note: "1 media hour, 720p, watermark" },
    trial: { v: "n/a", note: "Not stated on the pricing page" },
    metering: { v: "text", note: "Media hours plus AI credits" },
    rollover: { v: "n/a", note: "Not stated; Free credits are one-time" },
    refunds: { v: "text", note: "Within 48 hours of the invoice, no proration" },
    cancelInApp: { v: "n/a", note: "Not stated on the pricing page" },
  },
  billingTerms: {
    refunds:
      "Refunds are granted only within 48 hours of the invoice, and there is no proration for unused time, per the Descript refund policy checked Oct 8, 2026.",
    cancel: "Cancellation steps are not described on the pricing page.",
    sourceUrl: REFUNDS,
    checkedAt: CHECKED,
  },
  quotes: [
    {
      text: "Burned through 800 credits just trying to clean up filler pauses",
      date: "2026-10-02",
      url: "https://www.trustpilot.com/review/descript.com",
      evidences: "metering",
    },
    {
      text: "extremely difficult to cancel your subscription",
      date: "2026-09-25",
      url: "https://www.trustpilot.com/review/descript.com",
      evidences: "cancelInApp",
    },
  ],
  verification: {
    method: "fetch",
    date: CHECKED,
    note: "Pricing page fetched directly on Oct 8, 2026. Monthly and annual per-month figures are as the page states them, per person. Annual totals are not captured, so annualTotal is left out. The September 2025 switch to media hours plus credits is a third-party account (castmagic.io) and is cited as such in prose. Refund terms come from the Descript help center.",
  },
  seoPattern: "descript.com/compare/descript-vs-<competitor>, no page about Crayo",
};
